import type { UserGardenLocation } from '@/lib/userGardenLocation';
import {
  getStoredUserGardenLocation,
  getUserGardenLocationFromZip,
  saveStoredUserGardenLocation,
} from '@/lib/userGardenLocation';

// ── Zone names ────────────────────────────────────────────────────────────────

const ZONE_NAMES: Record<number, string> = {
  1:  'Zone 1 - Subarctic, extreme cold winters',
  2:  'Zone 2 - Very cold, very short growing season',
  3:  'Zone 3 - Cold winters, ~90-day frost-free season',
  4:  'Zone 4 - Cold winters, frost-free May - September',
  5:  'Zone 5 - Cold winters, growing season April - October',
  6:  'Zone 6 - Mild winters, good 7-month growing season',
  7:  'Zone 7 - Moderate winters, warm summers',
  8:  'Zone 8 - Mild winters, long 9-month growing season',
  9:  'Zone 9 - Warm winters, near year-round growing',
  10: 'Zone 10 - Frost-rare, near year-round growing',
  11: 'Zone 11 - Tropical, frost-free year-round',
  12: 'Zone 12 - Tropical, hot and humid year-round',
};

// ── Lookup cache ──────────────────────────────────────────────────────────────
// Caches individual zip → zone lookups so we don't re-fetch the same zip.

const CACHE_KEY_PREFIX = 'garden-zone-lookup-';

function getCachedLookup(zip: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + zip);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function setCachedLookup(zip: string, zone: number): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CACHE_KEY_PREFIX + zip, String(zone));
  } catch {
    // storage quota exceeded — skip caching silently
  }
}

// ── USDA API lookup ───────────────────────────────────────────────────────────

interface PhzmapiResponse {
  zone?: string;         // e.g. "7a", "10b", "6"
  zipcode?: string;
  temperature_range?: string;
  coordinates?: { lat: string; lon: string };
}

/**
 * Looks up the USDA hardiness zone for a 5-digit US zip code.
 * Uses the free USDA Plant Hardiness Zone API (phzmapi.org).
 * Results are cached in localStorage so repeated lookups are instant.
 *
 * Returns the integer zone (e.g. 7 for "7a", 10 for "10b"), or null on failure.
 */
export async function getZoneFromZip(zip: string): Promise<number | null> {
  if (!/^\d{5}$/.test(zip)) return null;

  // Return cached result if available
  const cached = getCachedLookup(zip);
  if (cached !== null) return cached;

  try {
    const res = await fetch(`https://phzmapi.org/${zip}.json`, {
      // Short timeout — we'd rather fall back to manual than hang
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;

    const data = (await res.json()) as PhzmapiResponse;
    if (!data.zone) return null;

    // "7a" → 7, "10b" → 10, "6" → 6
    const zone = parseInt(data.zone, 10);
    if (!Number.isFinite(zone) || zone < 1 || zone > 13) return null;

    setCachedLookup(zip, zone);
    return zone;
  } catch {
    // Network error, timeout, or JSON parse failure
    return null;
  }
}

// ── Helper functions ───────────────────────────────────────────────────────────

/** Returns a friendly display name for a zone number */
export function getZoneName(zone: number): string {
  return ZONE_NAMES[zone] ?? `Zone ${zone}`;
}

// ── localStorage persistence ──────────────────────────────────────────────────

const STORAGE_KEY = 'garden-user-zone';

interface ZoneData {
  zone: number;
  zip?: string;
  gardenLocation?: UserGardenLocation;
  setAt: string;
}

/** Returns the saved zone number, or null if not set / unavailable in SSR */
export function getCurrentZone(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ZoneData;
    return typeof data.zone === 'number' ? data.zone : null;
  } catch {
    return null;
  }
}

/** Saves the zone (and optional zip) to localStorage */
export function saveZone(zone: number, zip?: string, gardenLocation?: UserGardenLocation | null): void {
  if (typeof window === 'undefined') return;
  try {
    const data: ZoneData = { zone, setAt: new Date().toISOString() };
    if (zip) data.zip = zip;
    if (gardenLocation) data.gardenLocation = gardenLocation;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    saveStoredUserGardenLocation(gardenLocation ?? null);
  } catch {
    // quota exceeded or storage unavailable — fail silently
  }
}

export function getCurrentGardenLocation(): UserGardenLocation | null {
  return getStoredUserGardenLocation();
}

export function getCurrentZoneZip(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const direct = localStorage.getItem('garden-zone-zip');
    if (direct?.trim()) return direct.trim();

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ZoneData;
    return data.zip?.trim() || null;
  } catch {
    return null;
  }
}

export async function getGardenLocationFromZip(zip: string): Promise<UserGardenLocation | null> {
  return getUserGardenLocationFromZip(zip);
}

/** Clears the saved zone from localStorage */
export function clearZone(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    saveStoredUserGardenLocation(null);
  } catch {
    // ignore
  }
}
